import { formatDate, liters, money, quantityLabel, tons, whatsappNumber } from './format.js';
import { paymentTypeReceipt, t } from './i18n.js';

// WhatsApp deep links cannot carry a file attachment, so the message is a
// complete plain-text receipt plus a link back to the receipt page. The PDF
// button next to it produces the file itself if a copy is needed.
//
// The figures already carry bidirectional isolates from format.js, so amounts
// and dates keep their order inside the Arabic message.

function line(label, value) {
  return value === null || value === undefined || value === '' ? null : `${label}: ${value}`;
}

const join = (parts) =>
  parts
    .filter((part) => part !== null)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');

export function saleReceiptText(sale, settings = {}) {
  const facility = settings.facility_name || t.app.name;
  const amount = sale.currency === 'IQD' ? sale.amount_iqd : sale.amount_usd;
  return join([
    `*${facility}*`,
    t.doc.saleReceipt,
    `${t.sale.receiptNo}: ${sale.receipt_no}`,
    '',
    line(t.common.date, formatDate(sale.sold_on)),
    line(t.sale.customer, sale.customer_name),
    line(t.common.quantity, `${quantityLabel(sale.unit, sale.quantity)} (${liters(sale.liters)})`),
    line(t.shared.tank, sale.tank_name),
    line(t.common.amount, money(amount, sale.currency)),
    line(t.shared.payment, paymentTypeReceipt[sale.payment_type]),
    '',
    settings.receipt_footer || '',
    settings.facility_phone ? `${t.common.phone}: ${settings.facility_phone}` : '',
    `${window.location.origin}/sell/${sale.id}/receipt`,
  ]);
}

export function purchaseReceiptText(purchase, settings = {}) {
  const facility = settings.facility_name || t.app.name;
  const amount = purchase.currency === 'IQD' ? purchase.amount_iqd : purchase.amount_usd;
  return join([
    `*${facility}*`,
    t.doc.purchaseReceipt,
    `${t.purchase.batchNo}: ${purchase.batch_no}`,
    '',
    line(t.common.date, formatDate(purchase.purchased_on)),
    line(t.purchase.supplier, purchase.supplier_name),
    line(t.common.quantity, `${tons(purchase.tons)} / ${liters(purchase.liters)}`),
    line(t.shared.tank, purchase.tank_name),
    line(t.common.amount, money(amount, purchase.currency)),
    line(t.shared.payment, paymentTypeReceipt[purchase.payment_type]),
    '',
    settings.receipt_footer || '',
    settings.facility_phone ? `${t.common.phone}: ${settings.facility_phone}` : '',
    `${window.location.origin}/buy/${purchase.id}/receipt`,
  ]);
}

export function statementText(statement, settings = {}) {
  const owed = {
    customer: t.party.customers.statementOwed,
    supplier: t.party.suppliers.statementOwed,
    exchange: t.exchange.statementOwed,
    shareholder: t.shareholder.statementOwed,
  }[statement.party.type];
  const period =
    statement.from || statement.to
      ? `${t.doc.period}: ${statement.from ? formatDate(statement.from) : t.statement.start} - ${
          statement.to ? formatDate(statement.to) : t.statement.today
        }`
      : `${t.doc.period}: ${t.common.allTime}`;

  return join([
    `*${settings.facility_name || t.app.name}*`,
    `${t.doc.accountStatement} - ${statement.party.name}`,
    period,
    `${t.statement.entryCount(statement.entries.length)}`,
    '',
    `${owed}:`,
    `  ${money(statement.closing.iqd, 'IQD')}`,
    `  ${money(statement.closing.usd, 'USD')}`,
  ]);
}

/**
 * Opens WhatsApp with the message ready to send. With no phone number on file
 * it opens the contact picker instead of failing.
 */
export function openWhatsApp(text, phone, countryCode = '964') {
  const number = whatsappNumber(phone, countryCode);
  const url = number
    ? `https://wa.me/${number}?text=${encodeURIComponent(text)}`
    : `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}
